<template>
  <div class="contents subscriptions" v-loading="loading">
    <div class="title flexB">
      <h2>구독 관리</h2>
      <p class="summary">구독중 {{ total }}명</p>
    </div>

    <div class="filterArea">
      <div class="filterWrap flex">
        <el-select v-model="inputMode">
          <el-option label="이메일" value="email" />
          <el-option label="닉네임" value="nickname" />
        </el-select>

        <input
          type="text"
          v-model="searchWord"
          placeholder="검색어 입력"
          @keyup.enter="search"
        />
        <button class="basic" @click="search">검색</button>
        <button class="line reset" @click="reset">초기화</button>
      </div>
    </div>

    <div class="box one">
      <table>
        <colgroup>
          <col style="width: 6%" />
          <col style="width: 22%" />
          <col style="width: 14%" />
          <col style="width: 10%" />
          <col style="width: 15%" />
          <col style="width: 8%" />
          <col style="width: 10%" />
          <col style="width: 15%" />
        </colgroup>
        <tr>
          <th>번호</th>
          <th>이메일</th>
          <th>닉네임</th>
          <th>구독상태</th>
          <th>구독 만료일</th>
          <th>자동갱신</th>
          <th>가입경로</th>
          <th>가입일</th>
        </tr>
        <tr v-for="(data, i) in subscriberList" :key="data._id || i">
          <td>{{ total - ((currentPage - 1) * pageSize + i) }}</td>
          <td>
            <router-link to @click.native="handleDetail(data._id)">
              {{ data.email }}
            </router-link>
          </td>
          <td>{{ data.nickname || "-" }}</td>
          <td class="primary">{{ getSubscriptionLabel(data) }}</td>
          <td>{{ formatSubscribeDue(data.subscribeDue) }}</td>
          <td>{{ getAutoRenewingLabel(data.autoRenewing) }}</td>
          <td>{{ getAuthProviderLabel(data.authProvider) }}</td>
          <td>{{ moment(data.createdAt).format("YYYY.MM.DD HH:mm") }}</td>
        </tr>
      </table>
      <NoData v-if="isNodata" />
      <div class="pagination" v-if="subscriberList.length > 0">
        <el-pagination
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { fetchUserList } from "@/api/admin";
import NoData from "@/components/contents/Nodata";

export default {
  name: "subscriptionManage",
  components: { NoData },
  data() {
    return {
      moment,
      loading: false,
      inputMode: "",
      searchWord: "",
      subscriberList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      isNodata: false,
    };
  },
  created() {
    this.$store.dispatch("SET_NAVBAR", "8");
  },
  mounted() {
    this.syncFromRoute();
    this.getSubscriberList();
  },
  methods: {
    isWithdrawnUser(user) {
      return user.authProvider === "withdrawn" || user.dropedAt != null;
    },
    isActiveSubscriber(user) {
      return (
        !this.isWithdrawnUser(user) && user.subscriptionStatus === "active"
      );
    },
    getSubscriptionLabel(user) {
      return user?.subscriptionLabel || "구독중";
    },
    getAutoRenewingLabel(value) {
      if (value === true) {
        return "ON";
      }
      if (value === false) {
        return "OFF";
      }
      return "-";
    },
    getAuthProviderLabel(authProvider) {
      const authProviderMap = {
        apple: "애플",
        google: "구글",
        email: "이메일",
        withdrawn: "-",
      };
      return authProviderMap[authProvider] || authProvider || "-";
    },
    formatSubscribeDue(value) {
      return value ? moment(value).format("YYYY.MM.DD HH:mm") : "-";
    },
    async fetchAllUsers(params) {
      const countRes = await fetchUserList({
        ...params,
        page: 1,
        limit: 1,
      });
      const countPayload = countRes.data || {};

      if (countPayload.status !== 200) {
        throw new Error(
          countPayload.message || "회원 목록을 불러오지 못했습니다."
        );
      }

      const total = Number(countPayload.total || 0);
      if (total === 0) {
        return [];
      }

      const listRes = await fetchUserList({
        ...params,
        page: 1,
        limit: total,
      });
      const listPayload = listRes.data || {};

      if (listPayload.status !== 200) {
        throw new Error(
          listPayload.message || "회원 목록을 불러오지 못했습니다."
        );
      }

      return Array.isArray(listPayload.users) ? listPayload.users : [];
    },
    sortSubscribers(users = []) {
      return [...users].sort((a, b) => {
        const aDue = a.subscribeDue
          ? new Date(a.subscribeDue).getTime()
          : Infinity;
        const bDue = b.subscribeDue
          ? new Date(b.subscribeDue).getTime()
          : Infinity;
        return aDue - bDue;
      });
    },
    handleDetail(id) {
      this.$router.push({
        name: "userDetail",
        query: { id, from: "subscriptions", tab: "subscription" },
      });
    },
    syncFromRoute() {
      this.currentPage = Number(this.$route.query.page || 1);
    },
    pushQuery() {
      this.$router.push({
        name: "subscriptionManage",
        query: { page: String(this.currentPage) },
      });
    },
    reset() {
      this.inputMode = "";
      this.searchWord = "";
      this.currentPage = 1;
      this.pushQuery();
      this.getSubscriberList();
    },
    search() {
      if (this.searchWord && this.inputMode === "") {
        return alert("검색 조건을 선택해 주세요.");
      }
      this.currentPage = 1;
      this.pushQuery();
      this.getSubscriberList();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.pushQuery();
      this.getSubscriberList();
    },
    async getSubscriberList() {
      this.loading = true;
      try {
        const allUsers = await this.fetchAllUsers({
          inputMode: this.inputMode,
          searchWord: this.searchWord,
        });
        const filteredUsers = this.sortSubscribers(
          allUsers.filter((user) => this.isActiveSubscriber(user))
        );
        const start = (this.currentPage - 1) * this.pageSize;

        this.subscriberList = filteredUsers.slice(start, start + this.pageSize);
        this.total = filteredUsers.length;
        this.isNodata = this.total === 0;
      } catch (err) {
        alert(err.message || "구독 회원 목록을 불러오지 못했습니다.");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.filterArea {
  margin: 90px 30px 30px;
  width: calc(100% - 60px);
}

.filterArea .filterWrap {
  margin: 0;
  width: 100%;

}

.title .summary {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #e91e63;
}
</style>
